
import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, MenuIcon, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import Sidebar from "./Sidebar";
import { SessionContext } from "@/providers/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ThemeToggle } from "@/components/ui/brand/ThemeToggle";
import { BrandText } from "@/components/ui/brand";

interface MainLayoutProps {
  children: React.ReactNode;
  title?: string;
}

const MainLayout = ({ children, title }: MainLayoutProps) => {
  const { session } = useContext(SessionContext);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        toast({
          title: "Let's fix this together",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Signed out successfully",
          variant: "default",
        });
        navigate("/");
      }
    } catch (error) {
      console.error("Logout error:", error);
      toast({
        title: "Let's fix this together",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <main className="lg:pl-64 transition-all duration-300 ease-in-out min-h-screen">
        <div className="p-6 max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6 pb-4 border-b">
            <div className="flex items-center">
              <h1 className="text-2xl md:text-3xl font-bold text-brand flex items-center gap-2">
                <span>{title || "Dashboard"}</span>
                <Sparkles className="h-5 w-5 text-brand animate-pulse-gentle hidden md:inline-block" />
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <ThemeToggle variant="icon" />
              {session && (
                <Button variant="outline" onClick={handleLogout} className="gap-2 hover:bg-muted rounded-xl">
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline">Logout</span>
                </Button>
              )}
            </div>
          </div>
          <div className="animate-fade-in">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
};

export default MainLayout;
